import * as icons from '@mui/icons-material';
import ArticleIcon from '@mui/icons-material/Article';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PaymentIcon from '@mui/icons-material/Payment';
import {
  Box,
  Breadcrumbs,
  Divider,
  Drawer as MuiDrawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { signOut } from 'aws-amplify/auth';
import { OverflowTooltip } from 'components/common/overflow-tooltip';
import {
  CURRENT_AVAILABLE_GROUPS,
  getUserDisplayName,
  useAuthContext,
  useHasAuthGroup,
} from 'providers/auth';
import { usePageContext } from 'providers/page';
import {
  createContext,
  FC,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link as RRLink, Link as ReactRouterLink, useMatch, useResolvedPath } from 'react-router';
import routes from 'routes';
import { getAccounts, getActiveClientId } from 'utils/accounts';
import { clearAllAccountsAndTokens } from 'utils/accounts-logout';
import { getDocsUrl } from 'utils/docs-url';
import { thinScrollbarSx } from 'utils/scrollbar';
import AktifyLogoImage from '../../../public/aktify-logo-white.svg';
import { AccountSwitcher, PostAddNameDialog } from './account-switcher';
import AktifyIcon from './aktify-a-icon.svg';
import { BillingPortalModal } from './billing-portal-modal';
import NavDrawerBackground from './nav-drawer-bg.svg';
import { SidebarModeMenu } from './sidebar-mode-menu';

const docsUrl = getDocsUrl();

/** Long enough that crossing the rail toward the page does not trigger a peek. */
const PEEK_DELAY_MS = 150;

export const NARROW_DRAWER_WIDTH = 80;
export const DRAWER_WIDTH = 240;

// https://stackoverflow.com/questions/49784294/warning-received-false-for-a-non-boolean-attribute-how-do-i-pass-a-boolean-f
const AppIcon = styled(Box)<{ icon: 0 | 1 }>(({ icon }) => ({
  height: 24,
  content: `url(${icon ? AktifyIcon : AktifyLogoImage})`,
}));

const StyledLink = styled(ReactRouterLink)({
  // Fixed header row — doesn't scroll with the nav list below it. Left transparent so the drawer
  // paper's gradient runs unbroken from the logo down through the nav list.
  display: 'block',
  flexShrink: 0,
  backgroundColor: 'transparent',
  '&:hover': { backgroundColor: '#ffffff22' },
});

const NavHeaderLink: FC<{ children: ReactNode }> = ({ children }) => (
  <Box pl={3} py={2} my={2}>
    {children}
  </Box>
);

const drawerPaperProps: SxProps = {
  width: DRAWER_WIDTH,
  background: `url(${NavDrawerBackground})`,
  backgroundColor: '#000000fa',
  backgroundSize: 'cover',
  border: 'none',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const HomeLink: FC<{ showSmallIcon: boolean }> = ({ showSmallIcon }) => (
  <StyledLink to={routes.home.path}>
    <NavHeaderLink>
      <AppIcon icon={showSmallIcon ? 1 : 0} />
    </NavHeaderLink>
  </StyledLink>
);

interface DrawerProps {
  isParent?: boolean;
  showIcons?: true;
  showText?: boolean;
  narrow?: boolean;
  children?: ReactNode;
}

const DrawerContext: FC<DrawerProps> = ({ showText, isParent, narrow, children }) => {
  const isSmallScreen = useMediaQuery<Theme>((theme: Theme) => theme.breakpoints.down('md'));

  const [navDrawerContext, setNavDrawerContext] = useState<NavDrawerContextProps>({
    showText: showText || isSmallScreen,
    color: isParent ? 'parent' : 'child',
    narrow: (narrow && !isSmallScreen) || false,
  });

  useEffect(() => {
    setNavDrawerContext({
      showText: showText || isSmallScreen,
      color: isParent ? 'parent' : 'child',
      narrow: (narrow && !isSmallScreen) || false,
    });
  }, [isParent, narrow, showText, isSmallScreen]);

  return <NavDrawerContext.Provider value={navDrawerContext}>{children}</NavDrawerContext.Provider>;
};

const getInitials = (firstName: string | null | undefined, lastName: string | null | undefined) => {
  return (firstName?.charAt(0) ?? '') + (lastName?.charAt(0) ?? '');
};

const LeadChatCircle = styled(Box)(() => ({
  backgroundColor: 'white',
  borderRadius: '50%',
  // Match the 24px default MUI icon size of the sibling nav rows (Billing, Docs, Log Out) so the
  // avatar lines up with them instead of bulging.
  width: 24,
  height: 24,
  color: '#2E2E2E',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 'bold',
  fontSize: 12,
  lineHeight: 1,
  // Keep the fallback Person icon inside the smaller circle.
  '& svg': { fontSize: 16 },
}));

const UserAvatar: FC<{
  name?: string;
  email?: string;
  givenName?: string;
  familyName?: string;
}> = ({ name, givenName, familyName }) => {
  if (givenName || familyName) {
    return <LeadChatCircle>{getInitials(givenName, familyName)}</LeadChatCircle>;
  }
  if (name) {
    const nameSplit = name.split(' ');
    return <LeadChatCircle>{getInitials(nameSplit[0], nameSplit[1])}</LeadChatCircle>;
  }

  return (
    <LeadChatCircle>
      <icons.Person />
    </LeadChatCircle>
  );
};

const Drawer: FC<{ children?: ReactNode }> = ({ children }) => {
  const { user, isInternal } = useAuthContext();
  const isCustomerAdmin = useHasAuthGroup(CURRENT_AVAILABLE_GROUPS.CustomerAdmin);
  const {
    leftNavDrawerOpen,
    toggleLeftNavDrawer,
    sidebarCanPeek,
    sidebarMode,
    toggleManualCollapsed,
  } = usePageContext();
  const navContext = useContext(NavDrawerContext);
  // The pinned width, owned by the page above us; peeking must not change it. What the drawer
  // renders reads `contentContext` instead.
  const { narrow } = navContext;
  const isSmallScreen = useMediaQuery<Theme>((theme: Theme) => theme.breakpoints.down('md'));
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [switcherAnchor, setSwitcherAnchor] = useState<null | HTMLElement>(null);
  // Local, not context: a hover must not re-render every consumer of the page context.
  const [peeking, setPeeking] = useState(false); // auto, after PEEK_DELAY_MS
  const [hovering, setHovering] = useState(false); // manual, immediate
  const peekTimer = useRef<ReturnType<typeof setTimeout>>();

  const peek = sidebarCanPeek && peeking;
  const showEdgeHandle = sidebarMode === 'manual' && hovering && !isSmallScreen;
  // Pinned reserves layout width; peeking floats over the page, so the slot stays narrow.
  const slotWidth = narrow && !isSmallScreen ? NARROW_DRAWER_WIDTH : DRAWER_WIDTH;
  const paperWidth = peek ? DRAWER_WIDTH : slotWidth;

  // A peek widens the paper, so its contents must render expanded too — otherwise the drawer
  // opens to 240px of unlabelled icons.
  const contentContext = useMemo(
    () => (peek ? { ...navContext, narrow: false, showText: true } : navContext),
    [peek, navContext],
  );

  const startPeek = () => {
    // Only manual reads `hovering`; setting it in the other modes re-renders the nav for nothing.
    if (sidebarMode === 'manual') setHovering(true);
    if (!sidebarCanPeek) return;
    clearTimeout(peekTimer.current);
    peekTimer.current = setTimeout(() => setPeeking(true), PEEK_DELAY_MS);
  };
  const endPeek = () => {
    clearTimeout(peekTimer.current);
    setPeeking(false);
    setHovering(false);
  };
  useEffect(() => () => clearTimeout(peekTimer.current), []);
  // The card shows the active account so it's clear which one is in use; fall back to the user's
  // own details before any account has been named (single-account users).
  const activeAccount = getAccounts().find((a) => a.clientId === getActiveClientId());
  const accountLabel = activeAccount?.label || getUserDisplayName(user);
  const accountEmail = activeAccount?.email || user.email || '';

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (e) {
      console.log('error logging out', e);
    } finally {
      clearAllAccountsAndTokens();
    }
  };

  return (
    <>
      {/* Fixed, not inside the drawer: the paper's `overflow: hidden` would clip it at the edge. */}
      {showEdgeHandle && (
        <IconButton
          size="small"
          aria-label="Toggle navigation sidebar"
          aria-expanded={!narrow}
          onMouseEnter={startPeek}
          onClick={toggleManualCollapsed}
          sx={{
            position: 'fixed',
            // Centred on the logo header: my(16) + py(16) + 24px icon + py(16) + my(16) = 88,
            // so 44 - half the 28px handle.
            top: 30,
            left: `${slotWidth}px`,
            transform: 'translateX(-50%)',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            width: 28,
            height: 28,
            color: '#0A0A0A',
            backgroundColor: '#fff',
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 3px rgba(0,0,0,.12)',
            '&:hover': { backgroundColor: '#fff', color: 'error.main' },
          }}
        >
          {narrow ? (
            <icons.ChevronRight fontSize="small" />
          ) : (
            <icons.ChevronLeft fontSize="small" />
          )}
        </IconButton>
      )}
      <MuiDrawer
        variant={isSmallScreen ? 'temporary' : 'permanent'}
        open={isSmallScreen && leftNavDrawerOpen}
        onClose={toggleLeftNavDrawer}
        onMouseEnter={startPeek}
        onMouseLeave={endPeek}
        sx={{
          width: slotWidth,
          '& .MuiDrawer-paper': {
            ...drawerPaperProps,
            width: paperWidth,
            transition: 'width 220ms ease, box-shadow 220ms ease',
            // Only while peeking, so the shadow does not sit under a pinned sidebar.
            boxShadow: peek ? '8px 0 30px rgba(0,0,0,.25)' : 'none',
          },
        }}
      >
        <NavDrawerContext.Provider value={contentContext}>
          <HomeLink showSmallIcon={contentContext.narrow} />
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              overflowY: 'auto',
              overflowX: 'hidden',
              ...thinScrollbarSx('rgba(255, 255, 255, 0.3)'),
            }}
          >
            {children}

            <List sx={{ marginBottom: 4 }}>
              <li>
                <Divider color="#777f7c" sx={{ margin: 3 }} />
              </li>
              <ConditionalTooltip
                show={!contentContext.showText}
                title={
                  <>
                    {accountLabel}
                    {accountLabel && accountEmail && <br />}
                    {accountEmail}
                  </>
                }
              >
                <Box
                  role="button"
                  tabIndex={0}
                  aria-haspopup="true"
                  aria-expanded={Boolean(switcherAnchor)}
                  onClick={(e) => setSwitcherAnchor(e.currentTarget)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSwitcherAnchor(e.currentTarget);
                    }
                  }}
                  aria-label={!contentContext.showText ? accountLabel || accountEmail : undefined}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    // Left-align the avatar so it lands at the same 24px inset the sibling rows use
                    // for their icons (ListItemButton paddingLeft: 3), in both drawer states:
                    //   collapsed: mx 0  + pl 3 (24px) = 24px
                    //   expanded:  mx 2 (16px) + pl 1 (8px) = 24px
                    // The other rows are left-aligned, not centered, so centering would misalign.
                    justifyContent: 'flex-start',
                    gap: 1.5,
                    mx: contentContext.showText ? 2 : 0,
                    mb: 1,
                    pl: contentContext.showText ? 1 : 3,
                    pr: contentContext.showText ? 1.5 : 0,
                    py: 1,
                    borderRadius: contentContext.showText ? 2 : 0,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#ffffff14' },
                    '&:hover .switcher-chevron': { opacity: 1 },
                    '&:focus-visible': {
                      backgroundColor: '#ffffff14',
                      outline: '2px solid #ffffff8c',
                      outlineOffset: '-2px',
                      '.switcher-chevron': { opacity: 1 },
                    },
                  }}
                >
                  <UserAvatar {...user} />
                  {contentContext.showText && (
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <OverflowTooltip
                        typographyProps={{
                          sx: { color: '#fff', fontWeight: 600, lineHeight: 1.2 },
                        }}
                      >
                        {accountLabel}
                      </OverflowTooltip>
                      {accountEmail && (
                        <OverflowTooltip
                          typographyProps={{
                            sx: { color: '#ffffff8c', fontSize: 12, lineHeight: 1.3 },
                          }}
                        >
                          {accountEmail}
                        </OverflowTooltip>
                      )}
                    </Box>
                  )}
                  {contentContext.showText && (
                    <Box
                      className="switcher-chevron"
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        pl: 3,
                        pr: 1.5,
                        borderRadius: 2,
                        opacity: 0,
                        pointerEvents: 'none',
                        transition: 'opacity .15s',
                        background: 'linear-gradient(to right, transparent, #0d0d0d 24px)',
                      }}
                    >
                      <ExpandMoreIcon
                        sx={{
                          color: '#ffffff8c',
                          transform: switcherAnchor ? 'rotate(180deg)' : 'none',
                          transition: 'transform .15s',
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </ConditionalTooltip>
              {!isInternal && isCustomerAdmin && (
                <OnClickNavRow
                  onClick={() => setBillingModalOpen(true)}
                  icon={<PaymentIcon />}
                  text="Billing"
                />
              )}
              {docsUrl && (
                <OnClickNavRow
                  href={docsUrl}
                  icon={<ArticleIcon />}
                  text={
                    <>
                      Docs <OpenInNewIcon sx={{ fontSize: 'inherit', verticalAlign: 'middle' }} />
                    </>
                  }
                />
              )}
              <OnClickNavRow onClick={handleLogout} icon={<LogoutIcon />} text="Log Out" />
            </List>
          </Box>
          {!isSmallScreen && <SidebarModeMenu showText={contentContext.showText} />}
          <Popover
            open={Boolean(switcherAnchor)}
            anchorEl={switcherAnchor}
            onClose={() => setSwitcherAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            slotProps={{
              paper: {
                sx: {
                  width: DRAWER_WIDTH,
                  backgroundColor: '#1a1a1a',
                  color: '#fff',
                  border: '1px solid #ffffff1f',
                  borderRadius: 2,
                  mb: 1,
                  overflow: 'hidden',
                },
              },
            }}
          >
            <AccountSwitcher />
          </Popover>
          <PostAddNameDialog />
          <BillingPortalModal open={billingModalOpen} onClose={() => setBillingModalOpen(false)} />
        </NavDrawerContext.Provider>
      </MuiDrawer>
    </>
  );
};

const NavHeader: FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    px={1}
    py={1}
    my={2}
    sx={{
      position: 'sticky',
      color: 'white',
    }}
    zIndex={(theme) => theme.zIndex.drawer + 1}
  >
    {children}
  </Box>
);

const NavBreadcrumbs: FC<{ crumbs: string[] }> = ({ crumbs }) => (
  <Breadcrumbs aria-label="breadcrumb">
    {crumbs.map((crumb, idx) =>
      idx === crumbs.length - 1 ? (
        <Typography key={idx} color="text.primary">
          {crumb}
        </Typography>
      ) : (
        <Link key={idx} underline="hover" color="inherit" component={ReactRouterLink} to="/">
          {crumb}
        </Link>
      ),
    )}
  </Breadcrumbs>
);

const SelectedRectangle = styled('div')`
  align-self: stretch;
  background-color: #dd2424;
  position: relative;
  width: 8px;
`;

const NewNavItem = styled(ListItem)`
  display: flex;
  justify-content: space-between;
  position: relative;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Montserrat', Helvetica, serif;
  min-height: 54px;
  padding: 0;

  :hover {
    background-color: #ffffff22;
  }

  .child:hover {
    background: linear-gradient(270deg, rgba(46, 46, 46, 0.1) 0%, rgba(46, 46, 46, 0) 100%);
  }

  .child {
    color: rgba(46, 46, 46, 0.5);
  }

  .child.Mui-selected {
    color: rgba(46, 46, 46, 1);
    background: linear-gradient(270deg, rgba(46, 46, 46, 0.1) 0%, rgba(46, 46, 46, 0) 100%);
  }

  .parent.Mui-selected {
    color: white;
  }
`;

interface NavDrawerContextProps {
  showText: boolean;
  color: string;
  narrow: boolean;
}

export const NavDrawerContext = createContext<NavDrawerContextProps>({
  showText: false,
  color: 'child',
  narrow: false,
});

const ConditionalTooltip: FC<{ show: boolean; title: ReactNode; children: ReactElement }> = ({
  show,
  title,
  children,
}) => {
  return (
    <Tooltip
      title={show ? title : ''}
      placement="right-start"
      followCursor
      sx={{ fontSize: '10em' }}
    >
      {children}
    </Tooltip>
  );
};

const NavRow: FC<{
  to: string;
  text: string;
  icon?: ReactNode;
  isHome?: true;
  /** Renders the row with identical chrome but handles the click instead of navigating. */
  onClick?: () => void;
}> = ({ to, text, icon, isHome, onClick }) => {
  const resolved = useResolvedPath(to);
  const match = useMatch({ path: resolved.pathname, end: isHome ? true : to == '' });
  const { narrow, color, showText } = useContext(NavDrawerContext);
  const className = color ? color : 'child';

  return (
    <ConditionalTooltip show={narrow && !showText} title={text}>
      <NewNavItem>
        {/* Same spread idiom as OnClickNavRow below. */}
        <ListItemButton
          {...(onClick ? { onClick } : { component: RRLink, to, selected: !!match })}
          className={className}
          sx={{ paddingLeft: 3 }}
        >
          {icon && (
            <ListItemIcon sx={{ color: 'inherit', marginRight: -2, height: 24 }}>
              {icon}
            </ListItemIcon>
          )}
          {(showText || !icon) && <ListItemText className="text-wrapper" primary={text} />}
          {<ListItemText sx={{ color: 'inherit' }} />}
        </ListItemButton>
        {!onClick && match && <SelectedRectangle />}
      </NewNavItem>
    </ConditionalTooltip>
  );
};

const OnClickNavRow: FC<{
  onClick?: () => any;
  text: string | ReactNode;
  icon: ReactNode;
  href?: string;
}> = ({ onClick, text, icon, href }) => {
  const { showText } = useContext(NavDrawerContext);

  return (
    <ConditionalTooltip show={!showText} title={text}>
      <NewNavItem>
        <ListItemButton
          {...(href
            ? { component: 'a', href, target: '_blank', rel: 'noopener noreferrer' }
            : { onClick })}
          sx={{ paddingLeft: 3 }}
          aria-label={!showText && typeof text === 'string' ? text : undefined}
        >
          {icon && <ListItemIcon sx={{ color: 'inherit', marginRight: -2 }}>{icon}</ListItemIcon>}
          {showText && <ListItemText className="text-wrapper" primary={text} />}
        </ListItemButton>
      </NewNavItem>
    </ConditionalTooltip>
  );
};

export const NavDrawer = {
  List: List,
  Link: NavRow,
  OnClickNavRow,
  Breadcrumbs: NavBreadcrumbs,
  Divider: Divider,
  Header: NavHeader,
  Drawer: Drawer,
  Context: DrawerContext,
};
